// Thin wrapper over react-native-image-picker v7's native promise API.
// Returns a typed PickedAsset or null so callers don't have to deal with
// didCancel / errorCode / nested assets[0] each time.

import { Alert } from "react-native";
import {
  launchCamera,
  launchImageLibrary,
  type ImagePickerResponse,
  type CameraOptions,
  type ImageLibraryOptions,
} from "react-native-image-picker";

export interface PickedAsset {
  uri: string;
  fileName?: string;
  type?: string;
  fileSize?: number;
}

const toAsset = (r: ImagePickerResponse): PickedAsset | null => {
  if (!r || r.didCancel) return null;
  if (r.errorCode) {
    const msg =
      r.errorCode === "permission"
        ? "Permission denied. Please allow access in device settings."
        : r.errorCode === "camera_unavailable"
          ? "Camera is not available on this device."
          : r.errorMessage ?? "Could not open picker. Try again.";
    Alert.alert("Error", msg);
    return null;
  }
  const a = r.assets?.[0];
  if (!a?.uri) return null;
  return {
    uri: a.uri,
    fileName: a.fileName ?? `upload_${Date.now()}.jpg`,
    type: a.type ?? "image/jpeg",
    fileSize: a.fileSize ?? undefined,
  };
};

// Bounded dimensions keep the multipart upload under ~600 KB so the 10 s
// axios timeout in services/api.js doesn't trip on slow networks.
// Camera capture is the regression: stock camera output is 4000×3000+ and
// 4-6 MB at quality 0.8, which routinely times out where gallery photos
// (usually already downsized by the OS) don't. 1280×1280 is plenty for KYC
// OCR readability.
const MAX_DIMENSION = 1280;

const LIB_OPTIONS: ImageLibraryOptions = {
  mediaType: "photo",
  quality: 0.8,
  selectionLimit: 1,
  includeBase64: false,
  maxWidth: MAX_DIMENSION,
  maxHeight: MAX_DIMENSION,
};

const CAM_OPTIONS: CameraOptions = {
  mediaType: "photo",
  quality: 0.8,
  saveToPhotos: false,
  includeBase64: false,
  maxWidth: MAX_DIMENSION,
  maxHeight: MAX_DIMENSION,
};

/** Shows an Alert letting the user choose camera or gallery. */
export const pickImageWithChoice = (): Promise<PickedAsset | null> =>
  new Promise((resolve) => {
    Alert.alert("Upload Photo", "Choose source", [
      {
        text: "Camera",
        onPress: async () => {
          const r = await launchCamera({ ...CAM_OPTIONS, cameraType: "back" });
          resolve(toAsset(r));
        },
      },
      {
        text: "Gallery",
        onPress: async () => {
          const r = await launchImageLibrary(LIB_OPTIONS);
          resolve(toAsset(r));
        },
      },
      { text: "Cancel", style: "cancel", onPress: () => resolve(null) },
    ]);
  });

export const pickImageFromLibrary = async (): Promise<PickedAsset | null> => {
  const r = await launchImageLibrary(LIB_OPTIONS);
  return toAsset(r);
};

export const pickImageFromCamera = async (
  cameraType: "front" | "back" = "front",
): Promise<PickedAsset | null> => {
  const r = await launchCamera({ ...CAM_OPTIONS, cameraType });
  return toAsset(r);
};

export const recordVideoFromCamera = async (): Promise<PickedAsset | null> => {
  const r = await launchCamera({
    mediaType: "video",
    videoQuality: "medium",
    cameraType: "back",
    saveToPhotos: false,
  });
  return toAsset(r);
};
